import { RotatePanel } from "..";
import styles from "../AdvancedPanel.module.css";
import { useMetaVariableStore } from "../../../../store";
import { useAdvancedPanelTimeline } from "../useAdvancedPanelTimeline";
import { TimelineButton } from "./TimeLineButton";
import { useTimeline } from "../../../../../../contexts/TimelineProvider/TimelineProvider";
import { useObjectsState } from "../../../../../../contexts/ObjectsProvider";
import { Collapse } from "./Collapse";
import cursorControlDataEditor from "../../../../../../pageTypes/FMS_Player/components/keyDataEditor/cursorControlDataEditor";
import { ChangeEventHandler } from "react";

interface RotateProps {
  data: RotatePanel;
  onChange: () => void;
}

export function Rotate({ data, onChange }: RotateProps) {
  const value = useMetaVariableStore((s) => s.metaVariables[data.key] as string);
  const set = useMetaVariableStore((s) => s.setMetaVariable);
  const upsertAnimatedMetaVariable = useMetaVariableStore((s) => s.upsertAnimatedMetaVariable);
  const [tl] = useTimeline();
  const currentTime = tl?.scrubbingCurrentTime;
  const selectedObjectId = useObjectsState().selectedObjects[0]?.objectId;
  const { isObjectInTimeline, handleTimelineClick, metaVariableInCurrentTime } = useAdvancedPanelTimeline(data.key);

  const handleChange = (key: string, value: any) => {
    set(key, Number(value));
    if (isObjectInTimeline) {
      upsertAnimatedMetaVariable(key, currentTime, Number(value), selectedObjectId, true);
    }
    onChange();
  };

  const onTimelineClick = () => {
    handleTimelineClick(value, true);
    onChange();
  };

  return (
    <Collapse title={data.name}>
      <div className={styles.group} key={data.name}>
        <div className={styles.timelineWrapper}>
          <div className={styles.selectWrapper}>
            <RangeInput
              id={data.key}
              onInputChange={handleChange}
              value={Number(value)}
              label="Value"
              min={data.minValue}
              max={data.maxValue}
            />
          </div>
          {isObjectInTimeline && <TimelineButton onClick={onTimelineClick} active={!!metaVariableInCurrentTime} />}
        </div>
      </div>
    </Collapse>
  );
}

interface RangeInputProps {
  id: string;
  label: string;
  value: string | number;
  onInputChange: (key: string, value: any) => void;
  step?: number;
  disabled?: boolean;
  min: number;
  max: number;
}

export function RangeInput({ id, label, onInputChange, value, step, disabled, min, max }: RangeInputProps) {
  const shouldInvertSlider = min > max;

  const handleOnSliderChange = (e: any) => {
    const value = e.target.value;

    onInputChange(id, shouldInvertSlider ? value * -1 : value);
  };

  const handleOnInputChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    if (disabled) return;

    if (target instanceof HTMLInputElement) {
      const currentValue = Number(target.value);
      /**
       * To deal with the absolute vector we need to know which direction you are going
       */
      if (shouldInvertSlider) {
        if (currentValue < max) {
          target.value = String(max);
        } else if (currentValue > min) {
          target.value = String(min);
        }
      } else {
        if (currentValue < min) {
          target.value = String(min);
        } else if (currentValue > max) {
          target.value = String(max);
        }
      }
      /**
       * Just to avoid triggering the state
       */
      const shouldUpdate = shouldInvertSlider
        ? currentValue >= max && currentValue <= min
        : currentValue >= min && currentValue <= max;

      if (shouldUpdate) {
        onInputChange(id, currentValue);
      }
    }
  };

  return (
    <div className={styles.rangeInputContainer}>
      <label className={styles.rangeInputLabel}>
        <div>{label}</div>
        <div className={styles.rangeInputWrapper}>
          <input
            type="range"
            min={shouldInvertSlider ? min * -1 : min}
            max={shouldInvertSlider ? max * -1 : max}
            onChange={handleOnSliderChange}
            value={shouldInvertSlider ? Number(value) * -1 : Number(value)}
          />
          <input
            id={label ?? undefined}
            onChange={handleOnInputChange}
            type="number"
            value={value ?? min}
            step={step ?? 1}
          />
        </div>
      </label>
    </div>
  );
}
